<template>
  <div class="fixed inset-0 flex items-center justify-center z-50">
    <!-- Background Image with multiply blend effect -->
    <div class="fixed inset-0 -z-30">
      <img src="/assets/photo.jpg" alt="Background" class="w-full h-full object-cover opacity-50 bg-image-animate" />
    </div>
    
    <!-- Primary multiply blend overlay for rich color interaction -->
    <div class="fixed inset-0 -z-20 bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
    
    <!-- Secondary overlay for better text readability -->
    <div class="fixed inset-0 -z-10 bg-black/40 backdrop-blur-[2px]"></div>
    
    <!-- Additional dark base layer -->
    <div class="fixed inset-0 bg-gray-900" style="z-index: -40;"></div>

    <div
      class="relative z-10 mt-7 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 max-w-md w-full mx-4">
      <div class="p-4 sm:p-7">
        <div class="text-center">
          <router-link to="/" aria-label="Back to Engagr home" class="block">
            <img class="m-auto w-24 z-10 pb-4" :src="logoURLBLK" alt="Engagr Logo"/>
          </router-link>
          <h1 class="block text-1xl lg:text-2xl font-bold text-gray-800 dark:text-white">Sign in to Admin Dashboard</h1>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">Enter your credentials to access the admin panel</p>
        </div>

        <div class="mt-5">
          <!-- Forgot Password Form -->
          <div v-if="showForgotPassword" class="space-y-4">
            <div>
              <label for="forgot-email" class="block text-sm font-medium mb-2 dark:text-white">Email</label>
              <input 
                v-model="forgotEmail" 
                type="email" 
                id="forgot-email"
                class="py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                placeholder="Enter your email"
                required
              >
            </div>

            <div v-if="resetToken" class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <p class="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Reset Token Generated</p>
              <code class="block p-2 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded text-xs break-all mb-3">{{ resetToken }}</code>
              <p class="text-xs text-blue-700 dark:text-blue-300 mb-4">
                Copy this token and use it to reset your password below.
              </p>
              
              <div class="space-y-3">
                <div>
                  <label for="reset-token" class="block text-sm font-medium mb-2 dark:text-white">Reset Token</label>
                  <input 
                    v-model="resetPasswordToken" 
                    type="text" 
                    id="reset-token"
                    class="py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="Paste reset token"
                    required
                  >
                </div>
                <div>
                  <label for="new-password" class="block text-sm font-medium mb-2 dark:text-white">New Password</label>
                  <input 
                    v-model="newPassword" 
                    type="password" 
                    id="new-password"
                    class="py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="Min 8 characters, include uppercase, lowercase, and number"
                    required
                    minlength="8"
                  >
                </div>
                <button
                  type="button"
                  @click="handleResetPassword"
                  :disabled="isResettingPassword || !resetPasswordToken || !newPassword"
                  :class="{
                    'py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-transparent font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all text-sm w-full': true,
                    'bg-green-500 text-white hover:bg-green-600 focus:ring-green-500': !isResettingPassword && resetPasswordToken && newPassword,
                    'bg-gray-500 text-white cursor-not-allowed': isResettingPassword || !resetPasswordToken || !newPassword
                  }"
                >
                  <span v-if="isResettingPassword">Resetting...</span>
                  <span v-else>Reset Password</span>
                </button>
              </div>
            </div>

            <div v-if="forgotPasswordError" class="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
              {{ forgotPasswordError }}
            </div>

            <div v-if="forgotPasswordSuccess && !resetToken" class="text-green-500 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
              {{ forgotPasswordSuccess }}
            </div>

            <div class="flex gap-3">
              <button
                type="button"
                @click="showForgotPassword = false; resetForgotPassword()"
                class="flex-1 py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-gray-200 font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Back to Login
              </button>
              <button
                v-if="!resetToken"
                type="button"
                @click="handleForgotPassword"
                :disabled="isRequestingReset || !forgotEmail"
                :class="{
                  'py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-transparent font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all text-sm flex-1': true,
                  'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500': !isRequestingReset && forgotEmail,
                  'bg-gray-500 text-white cursor-not-allowed': isRequestingReset || !forgotEmail
                }"
              >
                <span v-if="isRequestingReset">Sending...</span>
                <span v-else>Send Reset Token</span>
              </button>
            </div>
          </div>

          <!-- Login Form -->
          <form v-else @submit.prevent="handleLogin">
            <div class="grid gap-y-4">
              <!-- Email Input -->
              <div>
                <label for="email" class="block text-sm font-medium mb-2 dark:text-white">Email</label>
                <div class="relative">
                  <input 
                    v-model="email" 
                    type="email" 
                    id="email" 
                    name="email"
                    class="py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="admin@example.com"
                    required
                    autocomplete="email"
                  >
                </div>
              </div>

              <!-- Password Input -->
              <div>
                <label for="password" class="block text-sm font-medium mb-2 dark:text-white">Password</label>
                <div class="relative">
                  <input 
                    v-model="password" 
                    :type="passwordFieldType" 
                    id="password" 
                    name="password"
                    class="py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="Enter password"
                    required
                    autocomplete="current-password"
                  >
                  <div 
                    class="absolute inset-y-0 right-0 pr-3 h-12 flex items-center cursor-pointer"
                    @click="togglePasswordView"
                  >
                    <div v-if="passwordFieldType === 'password'">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-400">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    </div>
                    <div v-else>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-400">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Error Message -->
              <div v-if="error" class="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                {{ error }}
              </div>

              <!-- Forgot Password Link -->
              <div class="text-right">
                <button
                  type="button"
                  @click="showForgotPassword = true"
                  class="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Forgot password?
                </button>
              </div>

              <!-- Submit Button -->
              <button 
                :disabled="isLoading || !email || !password" 
                :class="{
                  'py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-transparent font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all text-sm': true,
                  'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500 dark:focus:ring-offset-gray-800': !isLoading && email && password,
                  'bg-gray-500 text-white cursor-not-allowed': isLoading || !email || !password
                }" 
                type="submit"
              >
                <span v-if="isLoading">Signing in...</span>
                <span v-else>Sign In</span>
              </button>
            </div>
          </form>
          <!-- End Form -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { auth } from '@/utils/betterAuth';

export default {
  name: 'BetterAuthLogin',
  props: {
    noRedirect: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      email: '',
      password: '',
      passwordFieldType: 'password',
      error: null,
      isLoading: false,
      logoURLBLK: '/assets/engagr-blk.svg',
      // Forgot password state
      showForgotPassword: false,
      forgotEmail: '',
      resetToken: null,
      resetPasswordToken: '',
      newPassword: '',
      isRequestingReset: false,
      isResettingPassword: false,
      forgotPasswordError: null,
      forgotPasswordSuccess: null,
    };
  },
  methods: {
    async handleLogin() {
      this.error = null;
      this.isLoading = true;

      try {
        await auth.signIn(this.email, this.password);
        
        // Emit success event
        this.$emit('authenticated');
        
        // Redirect or let parent handle - only redirect if noRedirect prop is not set
        if (!this.noRedirect) {
          this.$router.push('/admin');
        }
      } catch (error) {
        this.error = error.message || 'Invalid credentials. Please try again.';
        console.error('Login error:', error);
      } finally {
        this.isLoading = false;
      }
    },
    togglePasswordView() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    },
    async handleForgotPassword() {
      this.forgotPasswordError = null;
      this.forgotPasswordSuccess = null;
      this.isRequestingReset = true;

      try {
        const response = await auth.forgotPassword(this.forgotEmail);
        if (response.resetToken) {
          // Development mode - token returned directly
          this.resetToken = response.resetToken;
          this.forgotPasswordSuccess = 'Reset token generated. Use it below to reset your password.';
        } else {
          // Production mode - token sent via email
          this.forgotPasswordSuccess = response.message || 'If the email exists, a reset link has been sent.';
        }
      } catch (error) {
        this.forgotPasswordError = error.message || 'Failed to generate reset token. Please try again.';
        console.error('Forgot password error:', error);
      } finally {
        this.isRequestingReset = false;
      }
    },
    async handleResetPassword() {
      this.forgotPasswordError = null;
      this.forgotPasswordSuccess = null;
      this.isResettingPassword = true;

      try {
        await auth.resetPassword(this.resetPasswordToken, this.newPassword);
        this.forgotPasswordSuccess = 'Password reset successfully! You can now sign in with your new password.';
        
        // Clear form and reset state after 2 seconds
        setTimeout(() => {
          this.resetForgotPassword();
          this.showForgotPassword = false;
        }, 2000);
      } catch (error) {
        this.forgotPasswordError = error.message || 'Failed to reset password. Please check your token and try again.';
        console.error('Reset password error:', error);
      } finally {
        this.isResettingPassword = false;
      }
    },
    resetForgotPassword() {
      this.forgotEmail = '';
      this.resetToken = null;
      this.resetPasswordToken = '';
      this.newPassword = '';
      this.forgotPasswordError = null;
      this.forgotPasswordSuccess = null;
    }
  }
};
</script>

<style scoped>
.bg-image-animate {
  animation: subtle-zoom 30s ease-in-out infinite;
}

@keyframes subtle-zoom {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}
</style>

